import { useElementWidth } from "@/hooks/useElementWidth";
import {
  FileText,
  ImageIcon,
  ListIcon,
  type LucideIcon,
  MessageSquare,
  ScrollText,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { TabsList, TabsTrigger } from "../ui/tabs";
import { VersionDropdownSlot } from "./VersionDropdownSlot";
import { MED_CHRON_VIEW_TABS } from "./constants";
import type { MedChronViewTab } from "./types";

/** "Version" prefix needs this many px in the slot; below that the name truncates to "V…". */
const VERSION_LABEL_MIN_WIDTH = 280;

const tabClass =
  "relative h-10 shrink-0 rounded-none border-b-2 border-transparent bg-transparent px-3 pb-2 pt-2 text-sm font-bold text-neutral-500 transition-all hover:text-neutral-700 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none sm:px-6";

const SCROLL_END_BUFFER = 4;

interface ViewTabItem {
  value: MedChronViewTab;
  label: string;
  Icon: LucideIcon;
  requiresCaseChat?: boolean;
}

const VIEW_TAB_ITEMS: ViewTabItem[] = [
  { value: MED_CHRON_VIEW_TABS.SUMMARY, label: "Summary", Icon: ScrollText },
  { value: MED_CHRON_VIEW_TABS.VISITS, label: "Visits", Icon: ListIcon },
  { value: MED_CHRON_VIEW_TABS.IMAGES, label: "Images", Icon: ImageIcon },
  { value: MED_CHRON_VIEW_TABS.FILES, label: "Files", Icon: FileText },
  {
    value: MED_CHRON_VIEW_TABS.CHATS,
    label: "Chats",
    Icon: MessageSquare,
    requiresCaseChat: true,
  },
];

export interface ViewTabsProps {
  caseChatEnabled?: boolean;
}

/** Must be used inside Tabs so value/onValueChange reach these triggers. */
export const ViewTabs: React.FC<ViewTabsProps> = ({ caseChatEnabled = false }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [versionSlotRef, versionSlotWidth] = useElementWidth<HTMLDivElement>();
  const isVersionCompact = versionSlotWidth < VERSION_LABEL_MIN_WIDTH;
  const [showStartFade, setShowStartFade] = useState(false);
  const [showEndFade, setShowEndFade] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const updateFade = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      const isAtStart = el.scrollLeft <= SCROLL_END_BUFFER;
      const isAtEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - SCROLL_END_BUFFER;
      setShowStartFade(hasOverflow && !isAtStart);
      setShowEndFade(hasOverflow && !isAtEnd);
    };

    updateFade();
    el.addEventListener("scroll", updateFade);
    const observer = new ResizeObserver(updateFade);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateFade);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 pt-1.5">
      <div className="relative min-w-[min(100%,40rem)] flex-1">
        <div
          aria-hidden
          className={`pointer-events-none absolute bottom-0 left-0 top-0 w-8 bg-gradient-to-r from-surface-subtle to-transparent transition-opacity duration-200 ${
            showStartFade ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          aria-hidden
          className={`pointer-events-none absolute bottom-0 right-0 top-0 w-8 bg-gradient-to-l from-surface-subtle to-transparent transition-opacity duration-200 ${
            showEndFade ? "opacity-100" : "opacity-0"
          }`}
        />
        <div ref={scrollRef} className="overflow-x-auto">
          <TabsList className="h-auto w-max min-w-full justify-start gap-0 bg-transparent p-0 sm:gap-2">
            {VIEW_TAB_ITEMS.filter((tab) => !tab.requiresCaseChat || caseChatEnabled).map((tab) => (
              <ViewTabTrigger key={tab.value} {...tab} />
            ))}
          </TabsList>
        </div>
      </div>
      <div ref={versionSlotRef} className="min-w-[200px] max-w-sm flex-1 basis-[280px]">
        <VersionDropdownSlot compact={isVersionCompact} />
      </div>
    </div>
  );
};

function ViewTabTrigger({ value, label, Icon }: ViewTabItem) {
  return (
    <TabsTrigger value={value} className={tabClass}>
      <div className="flex items-center gap-1.5 sm:gap-2.5">
        <Icon size={18} className="sm:hidden" />
        <Icon size={20} className="hidden sm:block" />
        <span>{label}</span>
      </div>
    </TabsTrigger>
  );
}
